import React, { createContext, useState, useEffect } from 'react';
import api from './api';
import { clearAll as clearSyncCache } from './SyncEngine';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Load cached user immediately so we skip the loading screen on return visits.
  // BUT only trust the cache if we still have a token — otherwise the user is
  // actually logged out and should go straight to the landing page.
  const [user, setUser] = useState(() => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        localStorage.removeItem('cached_user');
        return null;
      }
      const cached = localStorage.getItem('cached_user');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => {
    // Only show loading if we have a token but no cached user
    const hasToken = !!localStorage.getItem('access_token');
    const hasCachedUser = !!localStorage.getItem('cached_user');
    return hasToken && !hasCachedUser;
  });
  const [error, setError] = useState(null);

  // Persist user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('cached_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('cached_user');
    }
  }, [user]);

  // Validate auth in the background (never blocks the UI if we have a cached user)
  useEffect(() => {
    let didCancel = false;

    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        if (!didCancel) setLoading(false);
        return;
      }

      try {
        // Long timeout: the free-tier backend can take 30–60s to wake from sleep.
        // We'd rather wait than time out and bounce a logged-in user to the landing page.
        const { data } = await api.get('/api/auth/user', { timeout: 60000 });
        if (!didCancel) {
          // First load of this tab/session — if user has no password,
          // prompt them. Refreshes within the same tab won't re-prompt
          // because session_started persists until the tab is closed.
          if (!sessionStorage.getItem('session_started')) {
            sessionStorage.setItem('session_started', '1');
            if (data?.user && data.user.has_password === false) {
              sessionStorage.setItem('prompt_password_setup', '1');
            }
          }
          // Merge with previous user so fields that aren't returned by
          // /api/auth/user (like avatar_url saved via saveGoogleAvatar)
          // aren't wiped out on background validation. Also: don't clobber
          // a good avatar_url/name with null — Supabase's signed-URL call
          // sometimes hiccups and returns null, which would otherwise flash
          // the avatar into initials briefly.
          setUser((prev) => {
            const next = { ...prev, ...data.user };
            if (!next.avatar_url && prev?.avatar_url) next.avatar_url = prev.avatar_url;
            if (!next.name && prev?.name) next.name = prev.name;
            return next;
          });
        }
      } catch (err) {
        // The api.js interceptor handles 401/403 by silently refreshing the
        // access token. We only land here in two real scenarios:
        //   1. Refresh itself failed with 400/401/403 → interceptor cleared
        //      tokens. The session is genuinely dead → null the user.
        //   2. Anything else (timeout, network down, 5xx, server still
        //      waking up). Tokens are intact → KEEP the cached user logged
        //      in. The user explicitly asked to stay signed in until they
        //      log out, so a sleeping server must never bounce them to the
        //      landing page. Subsequent data fetches will retry on their own
        //      once the server is reachable.
        if (!localStorage.getItem('access_token')) {
          // Preserve the user's email so the landing page can pre-fill it
          // for a one-tap re-login. We only nuke `cached_user` (and friends)
          // on explicit logout(), not on session-expired bounces.
          try {
            const cached = localStorage.getItem('cached_user');
            if (cached) {
              const parsed = JSON.parse(cached);
              if (parsed?.email) localStorage.setItem('last_session_email', parsed.email);
            }
          } catch { /* ignore */ }
          localStorage.removeItem('refresh_token');
          if (!didCancel) setUser(null);
        } else if (!didCancel && !err.response) {
          setError('Could not reach the server. It might be waking up — your data will load shortly.');
        }
      } finally {
        if (!didCancel) setLoading(false);
      }
    };

    checkAuth();

    return () => {
      didCancel = true;
    };
  }, []);

  const signup = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post('/api/auth/signup', { email, password });
      setUser(data.user);
      return data;
    } catch (err) {
      setError(err.response?.data?.error || 'Sign up failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signin = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post('/api/auth/signin', { email, password });
      localStorage.setItem('access_token', data.session.access_token);
      localStorage.setItem('refresh_token', data.session.refresh_token);
      if (data?.user && data.user.has_password === false) {
        sessionStorage.setItem('prompt_password_setup', '1');
      }
      setUser(data.user);
      return data;
    } catch (err) {
      setError(err.response?.data?.error || 'Sign in failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signinWithGoogle = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/api/auth/google-url');
      if (!data?.url) {
        throw new Error('Google sign in URL not available');
      }
      window.location.assign(data.url);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Google sign in failed');
      setLoading(false);
      throw err;
    }
  };

  const completeOAuthSignin = async (accessToken, refreshToken) => {
    setLoading(true);
    setError(null);
    try {
      localStorage.setItem('access_token', accessToken);
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }

      const { data } = await api.get('/api/auth/user', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      // One-shot flag: prompt password setup on this sign-in only.
      // Cleared after the modal is shown/closed, so a page refresh won't re-prompt.
      if (data?.user && data.user.has_password === false) {
        sessionStorage.setItem('prompt_password_setup', '1');
      }
      setUser(data.user);
      return data.user;
    } catch (err) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setError(err.response?.data?.error || 'Google callback failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const saveGoogleAvatar = async () => {
    try {
      const { data } = await api.post('/api/profile/save-avatar', {});
      if (data?.avatar_url) {
        setUser((prev) => ({ ...prev, avatar_url: data.avatar_url }));
      }
      return data?.avatar_url;
    } catch (err) {
      // non-critical — don't block sign-in flow
      console.warn('Avatar save failed:', err.response?.data?.error || err.message);
    }
  };

  const updateUser = (fields) => {
    setUser((prev) => ({ ...prev, ...fields }));
  };

  const logout = async () => {
    // Clear local state FIRST so the UI updates immediately — even if the
    // backend call hangs (slow network, revoked session on another device,
    // server down), the user is out of this device instantly.
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('cached_user');
    localStorage.removeItem('cached_dashboards');
    localStorage.removeItem('cached_selected_dashboard_id');
    localStorage.removeItem('cached_user_defaults');
    localStorage.removeItem('last_session_email');
    sessionStorage.removeItem('session_started');
    sessionStorage.removeItem('prompt_password_setup');
    setUser(null);

    // Best-effort backend logout with a short timeout. Failures are non-blocking.
    try {
      await api.post('/api/auth/logout', {}, { timeout: 5000 });
    } catch (err) {
      console.warn('Backend logout failed (non-blocking):', err.message);
    }

    // Best-effort cache clear — doesn't block navigation.
    try {
      await clearSyncCache();
    } catch (err) {
      console.warn('Cache clear failed (non-blocking):', err);
    }
  };

  const resetPassword = async (email) => {
    setLoading(true);
    setError(null);
    try {
      await api.post('/api/auth/reset-password', { email });
      return { message: 'Password reset email sent' };
    } catch (err) {
      setError(err.response?.data?.error || 'Reset password failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        signup,
        signin,
        signinWithGoogle,
        completeOAuthSignin,
        saveGoogleAvatar,
        updateUser,
        logout,
        resetPassword,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
